import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { getConfigSync } from '../utils/config';
import SwaggerRepairAlert, {
  SwaggerRepairInfo,
  formatSwaggerUploadSuccess,
} from './SwaggerRepairAlert';

// Get API URL dynamically - will be updated when config.json loads
const getApiBaseUrl = () => getConfigSync().API_URL;

interface Api {
  id: string;
  name: string;
  description: string;
  api_url?: string;
  created_at: string;
  updated_at: string;
}

const ApiList: React.FC = () => {
  const [apis, setApis] = useState<Api[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [swaggerRepair, setSwaggerRepair] = useState<SwaggerRepairInfo | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  const navigate = useNavigate();
  const hasFetchedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    // Only fetch if user is authenticated and we haven't fetched yet
    if (user && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchApis();
    } else if (!user && !loading) {
      // If user is not authenticated and not loading, don't try to fetch
      setLoading(false);
    }
    
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const fetchApis = async () => {
    // Prevent multiple simultaneous requests
    if (!isMountedRef.current) return;
    
    try {
      setLoading(true);
      setError(null);
      const url = `${getApiBaseUrl()}/apis`;
      
      const response = await axios.get(url, {
        timeout: 5000,
        withCredentials: true,
        validateStatus: (status) => status < 500 // Don't throw on 4xx errors
      });
      
      if (!isMountedRef.current) return;
      
      if (response.status === 401) {
        // Don't show error - let the axios interceptor or ProtectedRoute handle redirect
        // The interceptor will redirect to /login
        setLoading(false);
        return;
      }
      
      if (response.status === 200) {
        if (Array.isArray(response.data)) {
          setApis(response.data);
          setError(null);
        } else {
          setApis([]);
          setError('Could not load APIs (unexpected response from the server).');
        }
      } else {
        throw new Error(`Unexpected status: ${response.status}`);
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;
      
      // If it's a redirect error from interceptor, don't show error
      if (err.message === 'Redirecting to login') {
        return;
      }
      
      let errorMsg = 'Failed to fetch APIs';
      if (err.code === 'ECONNABORTED') {
        errorMsg = 'Request timeout - backend may not be running';
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        errorMsg = 'Network error - cannot connect to backend. Is it running on port 43001?';
      } else if (err.response) {
        // Don't show error for 401 - it's handled by interceptor
        if (err.response.status === 401) {
          return;
        }
        errorMsg = `Server error: ${err.response.status} - ${err.response.data?.error || err.response.statusText}`;
      } else {
        errorMsg = err.message || 'Unknown error';
      }
      
      setError(errorMsg);
      setApis([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('swaggerFile', selectedFile);
      formData.append('name', name);
      formData.append('description', description);
      if (apiUrl) {
        formData.append('api_url', apiUrl);
      }

      const response = await axios.post(`${getApiBaseUrl()}/apis`, formData, {
        withCredentials: true,
        timeout: 120000,
      });
      const warnings = response.data?.warnings as string[] | undefined;
      const stats = response.data?.swaggerStats as { bytes?: number; pathCount?: number } | undefined;
      const repair = response.data?.swaggerRepair as SwaggerRepairInfo | undefined;
      let successMsg = formatSwaggerUploadSuccess('API uploaded successfully', stats);
      if (warnings?.length) successMsg += `. ${warnings.join(' ')}`;
      setSwaggerRepair(repair ?? null);
      setSuccess(successMsg);
      setOpen(false);
      setSelectedFile(null);
      setName('');
      setDescription('');
      setApiUrl('');
      fetchApis();
    } catch (err: any) {
      const data = err.response?.data;
      const message = data?.details
        ? `${data.error || 'Failed to upload API'}: ${data.details}`
        : (data?.error || 'Failed to upload API');
      setError(message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this API?')) {
      return;
    }

    try {
      await axios.delete(`${getApiBaseUrl()}/apis/${id}`, {
        withCredentials: true,
      });
      setSuccess('API deleted successfully');
      fetchApis();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete API');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
          API Portal
        </Typography>
        {user?.isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            Upload Swagger File
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <SwaggerRepairAlert repair={swaggerRepair} onClose={() => setSwaggerRepair(null)} />

      <Grid container spacing={3}>
        {apis.length === 0 ? (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center">
                  No APIs uploaded yet. Click "Upload Swagger File" to get started.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          apis.map((api: Api) => (
            <Grid item xs={12} md={6} lg={4} key={api.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {api.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {api.description || 'No description'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created: {new Date(api.created_at).toLocaleDateString()}
                  </Typography>
                  <Box mt={2} display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/apis/${api.id}`)}
                    >
                      View Details
                    </Button>
                    {user?.isAdmin && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(api.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Swagger File</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="API Name"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              placeholder="Add a few sentences about the overall purpose of the API..."
            />
            <TextField
              fullWidth
              label="API Host URL (Optional)"
              value={apiUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiUrl(e.target.value)}
              margin="normal"
              placeholder="https://api.example.com"
              helperText="The base URL where this API is hosted. Used for documentation and testing."
            />
            <Box sx={{ mt: 2 }}>
              <input
                accept=".json,.yaml,.yml"
                style={{ display: 'none' }}
                id="swagger-file-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="swagger-file-upload">
                <Button variant="outlined" component="span" fullWidth>
                  {selectedFile ? selectedFile.name : 'Select Swagger File'}
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!selectedFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiList;
